import React, { useState, useEffect } from "react";
import styles from "../App.module.scss";
import classNames from "classnames";
import CreateSite from "./modal/CreateSite/CreateSite";
import ViewSite from "./modal/ViewSite/ViewSite";
import ViewBlock from "./modal/ViewBlock/ViewBlock";
import PlacePin from "./modal/PlacePin/PlacePin";
import SearchBar from "components/SearchBar/SearchBar";
import TextPage from "components/TextPage/TextPage";
import Map from "components/Map/Map";
import useCurrentLocation from "hooks/useCurrentLocation";
import useSitePins from "hooks/useSitePins";
import AddSiteButton from "components/AddSiteButton/AddSiteButton";
import SideMenu from "components/SideMenu/SideMenu";
import ZoomButtons from "components/ZoomButtons/ZoomButtons";
import LSBLogo from "assets/LSBLogo";
import useIndexedDb from "hooks/useIndexedDb";
import { IBlock, IUserSite } from "database";

const MainScreen: React.FC = () => {
	const currentLocation = useCurrentLocation();
	const [refresh, setRefresh] = useState(true);
	const [selectedSite, setSelectedSite] = useState<IUserSite>();
	const [openSearch, setOpenSearch] = useState(true);
	const [openPlacePin, setOpenPlacePin] = useState(false);
	const [confirmClose, setConfirmClose] = useState(false);
	const [selectedTab, setSelectedTab] = useState("default");
	const [sideMenu, setSideMenu] = useState(false);
	const [highlightSite, setHighlightSite] = useState<number>();
	const [selectedModal, setSelectedModal] = useState("");
	const [selectedBlock, setSelectedBlock] = useState<IBlock | undefined>();
	const [mapPosition, setMapPosition] = useState<{
		latitude: number;
		longitude: number;
	}>();
	const [showSitesUpdated, setShowSitesUpdated] = useState(false);
	const [filter, setFilter] = useState("all");
	const [markerPosition, setMarkerPosition] = useState(currentLocation);
	useIndexedDb(refresh);
	useEffect(() => {
		setRefresh(false);
	}, []);
	useEffect(() => {
		setMarkerPosition(currentLocation);
	}, [currentLocation]);
	useEffect(() => {
		if (selectedSite) {
			setMarkerPosition({
				// @ts-ignore
				latitude: selectedSite?.latitude,
				// @ts-ignore
				longitude: selectedSite?.longitude,
				changed: true,
			});
		} else {
			setMarkerPosition(currentLocation);
		}
	}, [selectedSite]);
	const sitePins = useSitePins(filter, highlightSite);
	useEffect(() => {
		if (refresh === true) {
			setShowSitesUpdated(true);
			setTimeout(() => setShowSitesUpdated(false), 2000);
		}
	}, [refresh]);

	const doRefresh = () => {
		setRefresh(true);
		setTimeout(() => setRefresh(false), 1000);
	};

	const changeMapLocation = (latitude: string, longitude: string) => {
		setMapPosition({
			latitude: parseFloat(latitude),
			longitude: parseFloat(longitude),
		});
	};
	const [zoom, setZoom] = useState(5);
	useEffect(() => {
		if (currentLocation.changed) {
			setZoom(16);
		}
	}, [currentLocation.changed]);
	const zoomMapIn = () => {
		const newZoomLevel = zoom < 16 ? zoom + 1 : zoom;
		setZoom(newZoomLevel);
	};
	const zoomMapOut = () => {
		const newZoomLevel = zoom > 0 ? zoom - 1 : zoom;
		setZoom(newZoomLevel);
	};

	return (
		<>
			<div
				className={classNames(styles.toast, {
					[styles.toastEnter]: showSitesUpdated,
				})}
			>
				Sites updated
			</div>
			<div className={styles.App}>
				<Map
					zoom={zoom}
					pinData={sitePins}
					position={mapPosition ? mapPosition : currentLocation}
					siteSelected={selectedSite}
					onSiteSelected={(site) => {
						if (
							selectedModal !== "editSite" &&
							selectedModal !== "createSite"
						) {
							setSelectedSite(site);
							setSelectedModal("viewSite");
							setHighlightSite(site.userSiteId);
						} else {
							setConfirmClose(true);
						}
					}}
				/>
				<SearchBar
					refreshData={() => doRefresh()}
					refreshSearch={true}
					selectedFilter={filter}
					createNewSite={() => {
						if (
							selectedModal !== "editSite" &&
							selectedModal !== "createSite"
						) {
							setSelectedSite(undefined);
							setSelectedModal("createSite");
							setSelectedTab("AddSite");
						} else {
							setConfirmClose(true);
						}
					}}
					filterChange={(status) => {
						setFilter(status);
					}}
					openTab={() => setOpenSearch(true)}
					close={() => setOpenSearch(false)}
					openSideMenu={() => setSideMenu(!sideMenu)}
					status={openSearch}
					onSiteSelected={(site) => {
						if (
							selectedModal !== "editSite" &&
							selectedModal !== "createSite"
						) {
							setSelectedSite(site);
							setSelectedModal("viewSite");
							setHighlightSite(site.userSiteId);
						}
					}}
					changeMapLocation={(latitude, longitude) => {
						changeMapLocation(latitude, longitude);
					}}
				/>
				<AddSiteButton
					onClick={() => {
						if (selectedModal !== "editSite") {
							setSelectedSite(undefined);
							setSelectedTab("AddSite");
							setSelectedModal("createSite");
						} else {
							setConfirmClose(true);
						}
					}}
				/>
				<div className={styles.nrLogo}>
					<LSBLogo />
				</div>
				<ZoomButtons zoomIn={() => zoomMapIn()} zoomOut={() => zoomMapOut()} />
				<SideMenu
					open={sideMenu}
					close={() => setSideMenu(!sideMenu)}
					selectedMenuScreen={(screen) => setSelectedTab(screen)}
				/>
				{selectedModal === "viewSite" && selectedSite && (
					<ViewSite
						editSite={() => {
							setSelectedModal("editSite");
							setSelectedTab("AddSite");
						}}
						viewBlock={(block) => {
							setSelectedBlock(block);
						}}
						selectedSite={selectedSite}
						close={() => {
							setSelectedModal("");
							setSelectedSite(undefined);
							doRefresh();
						}}
					/>
				)}
				{selectedBlock && (
					<ViewBlock
						selectedBlock={selectedBlock}
						close={() => {
							setSelectedBlock(undefined);
						}}
					/>
				)}
				{openPlacePin && (
					<PlacePin
						close={() => setOpenPlacePin(false)}
						changeMarkerPosition={(location) => {
							setMarkerPosition({
								latitude: location.latitude,
								longitude: location.longitude,
								changed: true,
							});
						}}
						markerPosition={markerPosition}
					/>
				)}
				{(selectedModal === "createSite" || selectedModal === "editSite") && (
					<CreateSite
						doUpdate={() => null}
						triggerCheckToClose={confirmClose}
						site={selectedSite}
						openTab={(tab) => setSelectedTab(tab)}
						status={"AddSite"}
						close={() => {
							setSelectedModal("");
							doRefresh();
						}}
						onCloseConfirmed={() => setConfirmClose(false)}
						placePin={() => setOpenPlacePin(true)}
						markerPosition={markerPosition}
					/>
				)}
				<TextPage
					status={selectedTab}
					openTab={(tab) => setSelectedTab(tab)}
					close={() => setSelectedTab("default")}
					page="about"
				/>
			</div>
		</>
	);
};

export default MainScreen;
